import { Overpayment, RatePeriod, Scenario } from './types.js';

/**
 * Discounted periods as they apply: a refinance replaces the original contract, so every
 * period stops before the first refinance month (and one starting later is dropped).
 */
export function effectiveRatePeriods(s: Pick<Scenario, 'ratePeriods' | 'refinances'>): RatePeriod[] {
  const firstRefinance = Math.min(...s.refinances.map(r => r.month));
  return s.ratePeriods
    .filter(p => p.startMonth < firstRefinance)
    .map(p => (p.endMonth < firstRefinance ? p : { ...p, endMonth: firstRefinance - 1 }));
}

export function addMonthsToYearMonth(start: string, offset: number): string {
  const [yearStr, monthStr] = start.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr) - 1; // JS months 0-based
  const base = new Date(Date.UTC(year, month, 1));
  base.setUTCMonth(base.getUTCMonth() + offset);
  const y = base.getUTCFullYear();
  const m = base.getUTCMonth() + 1;
  const mm = m < 10 ? `0${m}` : `${m}`;
  return `${y}-${mm}`;
}

export function findActiveRate(monthIndex: number, baseRate: number, periods: RatePeriod[]): number {
  const match = periods.find(p => monthIndex >= p.startMonth && monthIndex <= p.endMonth);
  return match ? match.rateApr : baseRate;
}

export function isDiscountedMonth(monthIndex: number, periods: RatePeriod[]): boolean {
  return periods.some(p => monthIndex >= p.startMonth && monthIndex <= p.endMonth);
}

export function expandRecurringOverpayments(list: Overpayment[], horizonMonth: number): Overpayment[] {
  const expanded: Overpayment[] = [];
  list.forEach(item => {
    if (!item.recurring && !item.adaptive) {
      expanded.push({ ...item });
      return;
    }

    // `recurring` and `adaptive` are mutually exclusive (validateScenario
    // rejects both); if both slip through, the item is expanded once, as recurring.
    if (item.recurring) {
      const step = item.recurring.step ?? 1;
      const end = typeof item.recurring.endMonth === 'number' ? item.recurring.endMonth : horizonMonth;
      for (let m = item.month; m <= end; m += step) {
        expanded.push({ ...item, recurring: undefined, adaptive: undefined, month: m });
      }
    } else if (item.adaptive) {
      const step = item.adaptive.step ?? 1;
      const end = typeof item.adaptive.endMonth === 'number' ? item.adaptive.endMonth : horizonMonth;
      for (let m = item.month; m <= end; m += step) {
        expanded.push({
          ...item,
          month: m,
          // Keep adaptive data for later calculation
          adaptive: { ...item.adaptive }
        });
      }
    }
  });
  return expanded
    .filter(o => o.month <= horizonMonth)
    .sort((a, b) => a.month - b.month);
}
